import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { IoSearchOutline } from 'react-icons/io5';

const Markets = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [tickerData, setTickerData] = useState({});
    const [isConnected, setIsConnected] = useState(false);
    const wsRef = useRef(null);

    // WebSocket connection for real-time data
    useEffect(() => {
        const connectWebSocket = () => {
            try {
                // Binance WebSocket streams for popular trading pairs
                const streams = [
                    'btcusdt@ticker',
                    'ethusdt@ticker',
                    'adausdt@ticker',
                    'solusdt@ticker',
                    'dogeusdt@ticker',
                    'avaxusdt@ticker',
                    'ltcusdt@ticker',
                    'dotusdt@ticker',
                    'linkusdt@ticker',
                    'maticusdt@ticker',
                    'bnbusdt@ticker',
                    'xrpusdt@ticker'
                ];

                const wsUrl = `wss://stream.binance.com:9443/stream?streams=${streams.join('/')}`;
                const ws = new WebSocket(wsUrl);
                wsRef.current = ws;

                ws.onopen = () => {
                    console.log('Markets WebSocket connected');
                    setIsConnected(true);
                };

                ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        const ticker = data.data || data;

                        if (ticker && ticker.s) {
                            const symbol = ticker.s;
                            const price = parseFloat(ticker.c || 0);
                            const changePercent = parseFloat(ticker.P || 0);
                            const volume = parseFloat(ticker.v || 0);
                            const highPrice = parseFloat(ticker.h || 0);
                            const lowPrice = parseFloat(ticker.l || 0);

                            setTickerData(prev => ({
                                ...prev,
                                [symbol]: {
                                    symbol,
                                    price: price.toFixed(2),
                                    change: `${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(2)}%`,
                                    volume: (volume / 1000000).toFixed(2) + 'M',
                                    high24h: highPrice.toFixed(2),
                                    low24h: lowPrice.toFixed(2),
                                    timestamp: Date.now()
                                }
                            }));
                        }
                    } catch (error) {
                        console.error('WebSocket message error:', error);
                    }
                };

                ws.onclose = () => {
                    console.log('Markets WebSocket disconnected');
                    setIsConnected(false);
                    // Reconnect after 3 seconds
                    setTimeout(connectWebSocket, 3000);
                };

                ws.onerror = (error) => {
                    console.error('WebSocket error:', error);
                    setIsConnected(false);
                };

            } catch (error) {
                console.error('WebSocket connection error:', error);
            }
        };

        connectWebSocket();

        return () => {
            if (wsRef.current) {
                wsRef.current.close();
            }
        };
    }, []);

    // Coin metadata for display
    const coinMetadata = {
        'BTCUSDT': { name: 'BTC', fullName: 'Bitcoin', image: 'https://assets.coingecko.com/coins/images/1/large/bitcoin.png' },
        'ETHUSDT': { name: 'ETH', fullName: 'Ethereum', image: 'https://assets.coingecko.com/coins/images/279/large/ethereum.png' },
        'ADAUSDT': { name: 'ADA', fullName: 'Cardano', image: 'https://assets.coingecko.com/coins/images/975/large/cardano.png' },
        'SOLUSDT': { name: 'SOL', fullName: 'Solana', image: 'https://assets.coingecko.com/coins/images/4128/large/solana.png' },
        'DOGEUSDT': { name: 'DOGE', fullName: 'Dogecoin', image: 'https://assets.coingecko.com/coins/images/5/large/dogecoin.png' },
        'AVAXUSDT': { name: 'AVAX', fullName: 'Avalanche', image: 'https://assets.coingecko.com/coins/images/12559/large/Avalanche_Circle_RedWhite_Trans.png' },
        'LTCUSDT': { name: 'LTC', fullName: 'Litecoin', image: 'https://assets.coingecko.com/coins/images/2/large/litecoin.png' },
        'DOTUSDT': { name: 'DOT', fullName: 'Polkadot', image: 'https://assets.coingecko.com/coins/images/12171/large/polkadot.png' },
        'LINKUSDT': { name: 'LINK', fullName: 'Chainlink', image: 'https://assets.coingecko.com/coins/images/877/large/chainlink-new-logo.png' },
        'MATICUSDT': { name: 'MATIC', fullName: 'Polygon', image: 'https://assets.coingecko.com/coins/images/4713/large/matic-token-icon.png' },
        'BNBUSDT': { name: 'BNB', fullName: 'Binance Coin', image: 'https://assets.coingecko.com/coins/images/825/large/bnb-icon2_2x.png' },
        'XRPUSDT': { name: 'XRP', fullName: 'Ripple', image: 'https://assets.coingecko.com/coins/images/44/large/xrp-symbol-white-128.png' }
    };

    // Create dynamic data items from WebSocket data
    const marketData = Object.keys(coinMetadata).map((symbol, index) => {
        const metadata = coinMetadata[symbol];
        const realTimeData = tickerData[symbol];

        return {
            index,
            symbol,
            name: metadata.name,
            fullName: metadata.fullName,
            image: metadata.image,
            price: realTimeData?.price || '0.00',
            change: realTimeData?.change || '+0.00%',
            volume: realTimeData?.volume || '0.00M',
            marketCap: '0.00B', // This would need additional API call
            isUpdating: false
        };
    });

    // Overview cards data from WebSocket
    const overviewData = {
        mostSearched: [
            { symbol: 'XAUT', price: '4,352.41', change: '+2.61%', isPositive: true },
            { symbol: 'DOOD', price: '0.007506', change: '-1.18%', isPositive: false },
            { symbol: 'ASTER', price: '1.079', change: '-15.24%', isPositive: false }
        ],
        newListings: [
            { symbol: 'RECALL', time: '00:22:30' },
            { symbol: 'CLO', price: '0.3203', change: '-7.82%', isPositive: false },
            { symbol: 'AKE', price: '0.001680', change: '+3.80%', isPositive: true }
        ],
        topGainers: [
            { symbol: 'BAS', price: '0.1667', change: '+79.59%', isPositive: true },
            { symbol: 'ZKC', price: '0.3026', change: '+61.17%', isPositive: true },
            { symbol: 'KGEN', price: '0.4154', change: '+48.64%', isPositive: true }
        ],
        valueLeaders: [
            { symbol: 'BTC', price: tickerData['BTCUSDT']?.price || '104,712.81', change: tickerData['BTCUSDT']?.change || '-5.87%', isPositive: false },
            { symbol: 'ETH', price: tickerData['ETHUSDT']?.price || '3,732.09', change: tickerData['ETHUSDT']?.change || '-7.66%', isPositive: false },
            { symbol: 'SOL', price: tickerData['SOLUSDT']?.price || '177.53', change: tickerData['SOLUSDT']?.change || '-8.51%', isPositive: false }
        ]
    };

    return (
        <div className="markets-page">
            <div className="container">
                {/* Market Overview Section */}
                <div className="market-overview">
                    <div className="row">
                        <div className="col-lg-3">
                            <div className="overview-card">
                                <h6>En Çok Arananlar</h6>
                                {overviewData.mostSearched.map((item, index) => (
                                    <div key={index} className="coin-item">
                                        <span className="symbol">{item.symbol}</span>
                                        <span className="price">{item.price}</span>
                                        <span className={`change ${item.isPositive ? 'positive' : 'negative'}`}>{item.change}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="col-lg-3">
                            <div className="overview-card">
                                <h6>Yeni Listelemeler</h6>
                                {overviewData.newListings.map((item, index) => (
                                    <div key={index} className="coin-item">
                                        <span className="symbol">{item.symbol}</span>
                                        {item.time ? (
                                            <span className="time">{item.time}</span>
                                        ) : (
                                            <>
                                                <span className="price">{item.price}</span>
                                                <span className={`change ${item.isPositive ? 'positive' : 'negative'}`}>{item.change}</span>
                                            </>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="col-lg-3">
                            <div className="overview-card">
                                <h6>En Çok Kazananlar</h6>
                                {overviewData.topGainers.map((item, index) => (
                                    <div key={index} className="coin-item">
                                        <span className="symbol">{item.symbol}</span>
                                        <span className="price">{item.price}</span>
                                        <span className={`change ${item.isPositive ? 'positive' : 'negative'}`}>{item.change}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="col-lg-3">
                            <div className="overview-card">
                                <h6>Değer Liderleri</h6>
                                {overviewData.valueLeaders.map((item, index) => (
                                    <div key={index} className="coin-item">
                                        <span className="symbol">{item.symbol}</span>
                                        <span className="price">{item.price}</span>
                                        <span className={`change ${item.isPositive ? 'positive' : 'negative'}`}>{item.change}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="markets-main-content">
                    {/* Search */}
                    <div className="markets-search">
                        <div className="search-input-wrapper">
                            <IoSearchOutline size={20} />
                            <input
                                type="text"
                                placeholder="Ara"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Market Data Table */}
                    <div className="market-data-table">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Coin</th>
                                    <th>Fiyat(USD)</th>
                                    <th>24H 30D Değişim</th>
                                    <th>Piyasa Değeri(USD)</th>
                                    <th>24sa İşlem Değeri(USD)</th>
                                    <th>Son 7 günlük fiyat eğilimi</th>
                                </tr>
                            </thead>
                            <tbody>
                                {marketData.map((coin) => (
                                    <tr key={coin.symbol}>
                                        <td>{coin.index}</td>
                                        <td>
                                            <div className="coin-info">
                                                <img src={coin.image} alt={coin.name} width="24" height="24" />
                                                <div>
                                                    <div className="coin-name">{coin.name}</div>
                                                    <div className="coin-full-name">{coin.fullName}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{coin.price}</td>
                                        <td className={coin.change.includes('+') ? 'positive' : 'negative'}>{coin.change}</td>
                                        <td>{coin.marketCap}</td>
                                        <td>{coin.volume}</td>
                                        <td>
                                            <div className="sparkline">
                                                <svg width="100" height="30">
                                                    <polyline
                                                        points="0,20 20,15 40,18 60,12 80,16 100,14"
                                                        stroke={coin.change.includes('+') ? '#22c55e' : '#ef4444'}
                                                        strokeWidth="2"
                                                        fill="none"
                                                    />
                                                </svg>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Markets;
